import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Grid,
    Card,
    CardContent,
    CardActions,
    Chip,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    LinearProgress
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArchiveIcon from '@mui/icons-material/Archive';
import { personalTrainingPackAPI } from '../../services/api';
import { formatDate } from '../../utils/dateUtils';

interface PTPackManagerProps {
    memberProfileId: string;
    memberName: string;
}

interface PTPackage {
    id: string;
    title: string;
    totalSessions: number;
    remainingSessions: number;
    cost: number;
    notes?: string;
    purchaseDate: string;
    expirationDate?: string;
    archived: boolean;
    sessionRedemptions?: any[];
}

const PTPackManager: React.FC<PTPackManagerProps> = ({ memberProfileId, memberName }) => {
    const [packs, setPacks] = useState<PTPackage[]>([]);
    const [activePack, setActivePack] = useState<PTPackage | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    
    const [assignDialogOpen, setAssignDialogOpen] = useState(false);
    const [redemptionDialogOpen, setRedemptionDialogOpen] = useState(false);
    const [selectedPackForRedemptions, setSelectedPackForRedemptions] = useState<PTPackage | null>(null);
    
    const [formData, setFormData] = useState({
        title: '',
        totalSessions: 10,
        cost: 0,
        notes: '',
        expirationDate: ''
    });

    useEffect(() => {
        fetchPacks();
    }, [memberProfileId]);

    const fetchPacks = async () => {
        setLoading(true);
        try {
            const packsRes = await personalTrainingPackAPI.getMemberPacks(memberProfileId);
            setPacks(packsRes.data);
            
            // Get active pack
            const active = packsRes.data.find((p: PTPackage) => !p.archived && p.remainingSessions > 0);
            setActivePack(active || null);
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to load packs');
        } finally {
            setLoading(false);
        }
    };

    const handleAssignPack = async () => {
        try {
            await personalTrainingPackAPI.assignPack({
                memberProfileId,
                ...formData,
                expirationDate: formData.expirationDate || undefined
            });
            setSuccessMessage('Pack assigned successfully');
            setAssignDialogOpen(false);
            setFormData({
                title: '',
                totalSessions: 10,
                cost: 0,
                notes: '',
                expirationDate: ''
            });
            await fetchPacks();
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to assign pack');
        }
    };

    const handleArchivePack = async (packId: string) => {
        if (window.confirm('Archive this pack? This will make it inactive.')) {
            try {
                await personalTrainingPackAPI.archivePack(packId);
                setSuccessMessage('Pack archived successfully');
                await fetchPacks();
            } catch (err: any) {
                setError(err.response?.data?.message || 'Failed to archive pack');
            }
        }
    };

    const viewRedemptions = async (pack: PTPackage) => {
        try {
            const res = await personalTrainingPackAPI.getRedemptions(pack.id);
            setSelectedPackForRedemptions({ ...pack, sessionRedemptions: res.data.redemptions });
            setRedemptionDialogOpen(true);
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to load redemptions');
        }
    };

    const getPackStatus = (pack: PTPackage) => {
        if (pack.archived) return { label: 'Archived', color: 'default' as const };
        if (pack.remainingSessions === 0) return { label: 'Depleted', color: 'warning' as const };
        if (pack.expirationDate && new Date(pack.expirationDate) < new Date()) {
            return { label: 'Expired', color: 'error' as const };
        }
        return { label: 'Active', color: 'success' as const };
    };

    return (
        <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">Personal Training Packs</Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setAssignDialogOpen(true)}
                    size="small"
                >
                    Assign Pack
                </Button>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
            {successMessage && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>{successMessage}</Alert>}

            {/* Active Pack Summary */}
            {activePack && (
                <Card sx={{ mb: 3, backgroundColor: '#f5f5f5' }}>
                    <CardContent>
                        <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                            Active Pack: {activePack.title}
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid size={{xs:6, md:3}}>
                                <Typography variant="body2" color="text.secondary">Sessions Remaining</Typography>
                                <Typography variant="h5">{activePack.remainingSessions}</Typography>
                            </Grid>
                            <Grid size={{xs:6, md:3}}>
                                <Typography variant="body2" color="text.secondary">Total Sessions</Typography>
                                <Typography variant="h5">{activePack.totalSessions}</Typography>
                            </Grid>
                            <Grid size={{xs:6, md:3}}>
                                <Typography variant="body2" color="text.secondary">Pack Cost</Typography>
                                <Typography variant="h5">${activePack.cost}</Typography>
                            </Grid>
                            <Grid size={{xs:6, md:3}}>
                                <Typography variant="body2" color="text.secondary">Expires</Typography>
                                <Typography variant="body1">
                                    {activePack.expirationDate ? formatDate(activePack.expirationDate) : 'No expiration'}
                                </Typography>
                            </Grid>
                        </Grid>
                        <Box mt={2}>
                            <LinearProgress
                                variant="determinate"
                                value={(activePack.remainingSessions / activePack.totalSessions) * 100}
                                sx={{ height: 8, borderRadius: 4 }}
                            />
                        </Box>
                    </CardContent>
                    <CardActions>
                        <Button size="small" onClick={() => viewRedemptions(activePack)}>View History</Button>
                        <Button size="small" startIcon={<ArchiveIcon />} onClick={() => handleArchivePack(activePack.id)}>
                            Archive
                        </Button>
                    </CardActions>
                </Card>
            )}

            {/* All Packs Table */}
            <Paper>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Title</TableCell>
                            <TableCell align="center">Sessions</TableCell>
                            <TableCell align="center">Remaining</TableCell>
                            <TableCell align="right">Cost</TableCell>
                            <TableCell>Purchase Date</TableCell>
                            <TableCell>Expires</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {packs.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={8} align="center">
                                    <Typography variant="body2" color="text.secondary">
                                        No packs assigned yet
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            packs.map((pack) => {
                                const status = getPackStatus(pack);
                                return (
                                    <TableRow key={pack.id}>
                                        <TableCell>{pack.title}</TableCell>
                                        <TableCell align="center">{pack.totalSessions}</TableCell>
                                        <TableCell align="center">{pack.remainingSessions}</TableCell>
                                        <TableCell align="right">${pack.cost}</TableCell>
                                        <TableCell>{formatDate(pack.purchaseDate)}</TableCell>
                                        <TableCell>
                                            {pack.expirationDate ? formatDate(pack.expirationDate) : 'N/A'}
                                        </TableCell>
                                        <TableCell>
                                            <Chip label={status.label} color={status.color} size="small" />
                                        </TableCell>
                                        <TableCell align="right">
                                            <Button size="small" onClick={() => viewRedemptions(pack)}>
                                                History
                                            </Button>
                                            {!pack.archived && (
                                                <Button
                                                    size="small"
                                                    onClick={() => handleArchivePack(pack.id)}
                                                >
                                                    Archive
                                                </Button>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </Paper>

            {/* Assign Pack Dialog */}
            <Dialog open={assignDialogOpen} onClose={() => setAssignDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Assign PT Pack to {memberName}</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid size={{xs:12}}>
                            <TextField
                                fullWidth
                                label="Pack Title"
                                value={formData.title}
                                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                required
                                placeholder="e.g., 10-Session Package"
                            />
                        </Grid>
                        <Grid size={{xs:12, md:6}}>
                            <TextField
                                fullWidth
                                type="number"
                                label="Total Sessions"
                                value={formData.totalSessions}
                                onChange={(e) => setFormData({ ...formData, totalSessions: parseInt(e.target.value) })}
                                required
                                InputProps={{ inputProps: { min: 1 } }}
                            />
                        </Grid>
                        <Grid size={{xs:12, md:6}}>
                            <TextField
                                fullWidth
                                type="number"
                                label="Pack Cost"
                                value={formData.cost}
                                onChange={(e) => setFormData({ ...formData, cost: parseFloat(e.target.value) })}
                                required
                                InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                            />
                        </Grid>
                        <Grid size={{xs:12}}>
                            <TextField
                                fullWidth
                                type="date"
                                label="Expiration Date (Optional)"
                                value={formData.expirationDate}
                                onChange={(e) => setFormData({ ...formData, expirationDate: e.target.value })}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid size={{xs:12}}>
                            <TextField
                                fullWidth
                                label="Notes"
                                value={formData.notes}
                                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                multiline
                                rows={2}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAssignDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleAssignPack} variant="contained" disabled={!formData.title || formData.cost === 0}>
                        Assign Pack
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Redemption History Dialog */}
            <Dialog
                open={redemptionDialogOpen}
                onClose={() => setRedemptionDialogOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    Redemption History: {selectedPackForRedemptions?.title}
                </DialogTitle>
                <DialogContent>
                    {selectedPackForRedemptions?.sessionRedemptions && selectedPackForRedemptions.sessionRedemptions.length > 0 ? (
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Date</TableCell>
                                    <TableCell>Session Name</TableCell>
                                    <TableCell>Coach</TableCell>
                                    <TableCell>Redeemed At</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {selectedPackForRedemptions.sessionRedemptions.map((redemption: any, index: number) => (
                                    <TableRow key={index}>
                                        <TableCell>{formatDate(redemption.sessionDate)}</TableCell>
                                        <TableCell>{redemption.sessionName}</TableCell>
                                        <TableCell>{redemption.coachName}</TableCell>
                                        <TableCell>{formatDate(redemption.redeemedAt)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                            No sessions redeemed yet
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRedemptionDialogOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default PTPackManager;
